import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';

import { FormField } from '@/components/shared/form-field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getErrorMessage } from '@/lib/errors';

import { requestPasswordReset } from '../api';
import { AuthLayout } from '../components/auth-layout';
import { FormAlert } from '../components/form-alert';
import { forgotPasswordSchema, type ForgotPasswordValues } from '../schemas';

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async ({ email }) => {
    setFormError(null);
    try {
      await requestPasswordReset(email, `${window.location.origin}/restablecer-contrasena`);
      setSent(true);
    } catch (error) {
      setFormError(getErrorMessage(error));
    }
  });

  return (
    <AuthLayout
      title="Recuperar contraseña"
      description="Te enviaremos un enlace para definir una nueva contraseña."
    >
      {sent ? (
        <FormAlert tone="success">
          Si el correo pertenece a una cuenta activa, recibirás un enlace en los próximos minutos.
        </FormAlert>
      ) : (
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <FormAlert>{formError}</FormAlert>}
          <FormField label="Correo" error={errors.email?.message} required>
            {(control) => (
              <Input
                {...control}
                {...register('email')}
                type="email"
                autoComplete="username"
                inputMode="email"
              />
            )}
          </FormField>
          <Button type="submit" className="w-full" size="lg" loading={isSubmitting}>
            Enviar enlace
          </Button>
        </form>
      )}
      <div className="mt-6 text-center">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Volver a iniciar sesión
        </Link>
      </div>
    </AuthLayout>
  );
}
